import { useQuery } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '../../api/client';

export type Quotation = Schemas['Quotation'];
export type QuotationSummary = Schemas['QuotationList']['data'][number];
export type QuotationLine = Quotation['lines'][number];
export type QuotationStatus = Quotation['status'];
export type Product = Schemas['Product'];
export type TaxRate = Schemas['TaxRate'];

export const quotationKeys = {
  all: ['quotations'] as const,
  list: (q: object) => ['quotations', 'list', q] as const,
  statusCount: (status: QuotationStatus) => ['quotations', 'count', status] as const,
  detail: (id: string) => ['quotations', 'detail', id] as const,
  order: (id: string) => ['quotations', 'order', id] as const,
};

export const STATUS_LABELS: Record<QuotationStatus, string> = {
  DRAFT: 'Draft',
  SENT: 'Sent',
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  EXPIRED: 'Expired',
  CONVERTED: 'Converted',
};

export const STATUSES = Object.keys(STATUS_LABELS) as QuotationStatus[];

/** Header params for POSTs (the generated types require the idempotency key). */
export function idem(key: string = crypto.randomUUID()) {
  return { 'idempotency-key': key };
}

export interface QuotationActions {
  edit: boolean;
  send: boolean;
  accept: boolean;
  reject: boolean;
  reopen: boolean;
  convert: boolean;
  remove: boolean;
}

/**
 * What the user may do with a quotation in this status — mirrors the API's transition table
 * (DRAFT → SENT/ACCEPTED/REJECTED; SENT → DRAFT/ACCEPTED/REJECTED; ACCEPTED → DRAFT/REJECTED;
 * REJECTED → DRAFT; CONVERTED is final). EXPIRED is a stored DRAFT or SENT past its validity: it can
 * still be edited (to extend it), rejected or deleted, but not sent, accepted or converted.
 */
export function quotationActions(
  status: QuotationStatus,
  can: (...permissions: string[]) => boolean,
): QuotationActions {
  const update = can('quotations.update');
  const open = status === 'DRAFT' || status === 'SENT';
  return {
    edit: update && (open || status === 'EXPIRED'),
    send: update && status === 'DRAFT',
    accept: update && open,
    reject: update && (open || status === 'ACCEPTED' || status === 'EXPIRED'),
    reopen: update && (status === 'SENT' || status === 'ACCEPTED' || status === 'REJECTED'),
    convert: can('quotations.convert', 'sales.create') && (open || status === 'ACCEPTED'),
    remove: update && (open || status === 'REJECTED' || status === 'EXPIRED'),
  };
}

/** Whole days from `today` to `date` (both "YYYY-MM-DD"); negative when it has passed. */
export function daysUntil(date: string, today: string): number {
  const ms = Date.parse(`${date}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`);
  return Math.round(ms / 86_400_000);
}

/** "YYYY-MM-DD" plus n days (calendar arithmetic in UTC, no time-zone drift). */
export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function validityText(validUntil: string | null, today: string): string {
  if (!validUntil) return 'No expiry';
  const n = daysUntil(validUntil, today);
  if (n < 0) return `Expired ${String(-n)} day${n === -1 ? '' : 's'} ago`;
  if (n === 0) return 'Expires today';
  return `${String(n)} day${n === 1 ? '' : 's'} left`;
}

export function useTaxRates(enabled = true) {
  return useQuery({
    queryKey: ['tax-rates'],
    queryFn: () => unwrap(api.GET('/tax-rates')),
    staleTime: 5 * 60_000,
    enabled,
    select: (d) => d.data,
  });
}

export function productQuery(productId: string) {
  return {
    queryKey: ['products', 'detail', productId] as const,
    queryFn: () => unwrap(api.GET('/products/{id}', { params: { path: { id: productId } } })),
    staleTime: 5 * 60_000,
  };
}

export interface UnitOption {
  unitId: string;
  symbol: string;
  name: string;
  factor: string;
}

/** The units an item can be quoted in: the base unit first, then the product's alternate units. */
export function unitOptions(product: Pick<Product, 'baseUnit' | 'units'>): UnitOption[] {
  const base: UnitOption = {
    unitId: product.baseUnit.id,
    symbol: product.baseUnit.symbol,
    name: product.baseUnit.name,
    factor: '1',
  };
  const alternates = product.units
    .filter((u) => u.unitId !== base.unitId)
    .map((u) => ({ unitId: u.unitId, symbol: u.symbol, name: u.name, factor: u.factor }));
  return [base, ...alternates];
}

/** The default sale unit (falls back to the base unit). */
export function defaultSaleUnit(product: Pick<Product, 'baseUnit' | 'units'>): UnitOption {
  const options = unitOptions(product);
  const preferred = product.units.find((u) => u.isDefaultSale);
  return options.find((o) => o.unitId === preferred?.unitId) ?? (options[0] as UnitOption);
}
